import type {
  Ticket,
  TicketActionWithSeverity,
  TicketDetails,
} from "@/_store/tickets/tickets.module.ts";
import {
  quickFixStepsMap,
  TicketAction,
  TicketActionSeverity,
  TicketTrigger,
  ticketTriggerIcons,
  TicketType,
  ticketTypeIcons,
  ViolationDirection,
  endpointDataGovernanceSubTriggers,
  userDataGovernanceSubTriggers,
  userDataGovernanceUnifiedTriggers,
  endpointDataGovernanceUnifiedTriggers,
} from "@/constants/tickets.ts";
import { union } from "lodash";
import { EdrProcessStatus } from "@/constants/edr.ts";
import intersection from "lodash/intersection";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import pullAllWith from "lodash/pullAllWith";
import { arrayToLowerCase, componentDialogsConfigConstructor } from "@/_helpers/utils.ts";
import { i18n } from "@/plugins/i18n.ts";
import type { ISelection } from "@/_store/selector.module.ts";
import type { DialogConfig } from "@/_store/dialogs.module.ts";
import TicketConfirmationModal from "@/components/modals/TicketConfirmationModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { Service } from "@/constants/cloud-apps";

export interface ContactUserFormData {
  includeEventDetails: boolean;
  contactMessage: string;
  recipients: string[];
}

export interface AllowBlockEmailFormData {
  action: TicketAction;
  closeTicket: boolean;
  metadata: {
    allRecipients: boolean;
    recipients: string[];
  };
}

export interface QuickFixMetadata {
  [TicketAction.CONTACT_USER]?: ContactUserFormData;
  [TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?: AllowBlockEmailFormData;
  selectedActions: TicketAction[];
}

export interface QuickFixPayload<T = TicketDetails> {
  ticket: T;
  metadata: QuickFixMetadata;
}

/**
 * Ticket actions that represent approve email variants.
 */
export const approveEmailActions = [
  TicketAction.APPROVE_EMAILS_FROM_DOMAIN,
  TicketAction.APPROVE_EMAILS_FROM_SENDER,
  TicketAction.APPROVE_IP,
  TicketAction.APPROVE_EMAIL,
];

/**
 * Ticket actions that represent discard email variants.
 */
export const discardEmailActions = [
  TicketAction.DISCARD_EMAILS_FROM_DOMAIN,
  TicketAction.DISCARD_EMAILS_FROM_SENDER,
  TicketAction.DISCARD_IP,
  TicketAction.DISCARD_EMAIL,
];

export const shouldShowUsersNumber = (ticketType: TicketType) => {
  return [TicketType.SIMULATED_EMAIL_PHISHING, TicketType.SECURITY_TRAINING_FAILURE].includes(
    ticketType
  );
};

export const getTicketDisplayedUsers = (ticket: Ticket) => {
  if (
    [TicketType.EMAIL_PHISHING, TicketType.MALWARE_IN_EMAIL_ATTACHMENTS].includes(ticket.eventType)
  ) {
    return ticket.to ? ticket.to : [];
  }
  if (ticket.lastLoggedInUsers?.length) {
    return ticket.lastLoggedInUsers ?? [];
  }
  const violationDirection = ticket.userDataAccessViolation?.violationDirection;
  if (violationDirection === ViolationDirection.ACCESS) {
    return union(
      ticket.userDataAccessViolation.users,
      ticket.potentiallyProtectableUsers
    ) as string[];
  } else if (violationDirection === ViolationDirection.SHARE) {
    return ticket.userDataAccessViolation.users as string[];
  }
  return ticket.from ? [ticket.from] : [];
};

export const getIconName = ({ eventType, eventTrigger }: Ticket) => {
  return ticketTypeIcons[eventType] || ticketTriggerIcons[eventTrigger];
};

export const showProcessStatus = (item: Ticket) => {
  return (
    item.processStatus === EdrProcessStatus.ALLOWED ||
    item.processStatus === EdrProcessStatus.BLOCKED
  );
};

export const getPotentiallyProtectableUsers = (ticket: Ticket) => {
  if (
    ticket.eventType === TicketType.EMAIL_PHISHING &&
    ticket.potentiallyProtectableUsers?.length
  ) {
    return ticket.potentiallyProtectableUsers;
  }

  return intersection(
    arrayToLowerCase(ticket.potentiallyProtectableUsers ?? []),
    arrayToLowerCase(getTicketDisplayedUsers(ticket))
  );
};

export const sortTicketTriggers = (triggers: TicketTrigger[]) => {
  const sortedTriggers: TicketTrigger[] = triggers
    .filter((e) => e !== TicketTrigger.EMAIL_PHISHING)
    .sort((a, b) =>
      i18n.global
        .t(`tickets.eventTriggers.${a}`)
        .localeCompare(i18n.global.t(`tickets.eventTriggers.${b}`))
    );

  if (triggers.includes(TicketTrigger.EMAIL_PHISHING)) {
    sortedTriggers.push(TicketTrigger.EMAIL_PHISHING);
  }

  return sortedTriggers;
};

export function getTicketConfirmationModalConfig({
  action,
  selectedItems,
  selection,
  selectedItemsCount,
  showCheckbox,
  callback,
  disable,
}: {
  action: TicketAction;
  selectedItems: Ticket[];
  selection: ISelection<Ticket> | Ticket[];
  selectedItemsCount: number;
  showCheckbox: boolean;
  callback: (data: any) => Promise<void>;
  disable: boolean;
}): DialogConfig {
  const singleItem = selectedItems.length === 1 ? selectedItems[0] : null;
  let userDataAccessViolationInterpolations = {};
  if (singleItem) {
    userDataAccessViolationInterpolations = {
      violationUsers: (singleItem.userDataAccessViolation?.users ?? []).join(","),
      violationDirection:
        singleItem.userDataAccessViolation?.violationDirection ?? ViolationDirection.SHARE,
      violationTrigger: i18n.global.t(
        `ticketDetails.fullDetails.accessViolations.${singleItem.eventTrigger}`
      ),
    };
  }

  return componentDialogsConfigConstructor({
    item: {
      ...(singleItem ?? {}),
      selection: selection,
      showCheckbox,
      checkboxText: i18n.global.t("general.closeUponConfirmation"),
      text: i18n.global.t(
        `modals.${action}.description`,
        {
          ...userDataAccessViolationInterpolations,
        },
        selectedItemsCount
      ),
    },
    action,
    callback,
    component: TicketConfirmationModal,
    disable,
    width: ModalWidth.SMALL,
  });
}

/**
 * Generic function to apply quick fix actions for tickets
 * @param payload - The quick fix payload containing ticket and metadata
 * @param getQuickFixCallback - Function that returns the appropriate callback for each action
 */
export const applyQuickFixAction = async <T, U>(
  payload: DialogDataConfig<QuickFixPayload<T>>,
  getQuickFixCallback: (
    action: TicketAction,
    payload: DialogDataConfig<QuickFixPayload<T>>
  ) => Promise<U>
) => {
  const snackbarStore = useSnackbarStore();
  try {
    const { selectedActions } = payload.item.metadata!;
    const actionPromises = selectedActions.map((action) => getQuickFixCallback(action, payload));

    await Promise.all(actionPromises);

    const appliedActions = selectedActions
      .map((action) => {
        if (action === TicketAction.DISCARD_EMAIL_WITH_OPTIONS) {
          const discardAction =
            payload.item.metadata[TicketAction.DISCARD_EMAIL_WITH_OPTIONS]?.action;
          return i18n.global.t(`ticketDetails.eventActions.${discardAction}`);
        }
        return i18n.global.t(`ticketDetails.eventActions.${action}`);
      })
      .join(", ");

    snackbarStore.addGenericSuccess(
      i18n.global.t(`snackbar.messages.event.${payload.action}`, { appliedActions })
    );
  } catch (err) {
    console.error(err);
  }
};

/**
 * Builds and sorts the ticket actions list, merging approve/discard email actions
 * into combined actions and handling outbound proxy variants.
 */
export const getTicketActionsList = (
  actions: TicketActionWithSeverity[],
  serviceName?: Service | string
): TicketActionWithSeverity[] => {
  const filteredActions = cloneDeep(actions);
  const approveTicketActions = filteredActions.filter((action) =>
    approveEmailActions.includes(action.name)
  );
  const discardTicketActions = filteredActions.filter((action) =>
    discardEmailActions.includes(action.name)
  );

  if (approveTicketActions.length) {
    if (serviceName === Service.OUTBOUND_PROXY) {
      const approveEmailAction = actions.find(
        (action) => action.name === TicketAction.APPROVE_EMAIL
      );
      filteredActions.push({
        name: TicketAction.APPROVE_OUTBOUND_PROXY_EMAIL,
        severity: approveEmailAction?.severity ?? TicketActionSeverity.MEDIUM,
      });
    } else {
      filteredActions.push({
        name: TicketAction.APPROVE_EMAIL_WITH_OPTIONS,
        severity: approveTicketActions.at(0)?.severity ?? TicketActionSeverity.MEDIUM,
      });
    }
    pullAllWith(filteredActions, approveTicketActions, isEqual);
  }

  if (discardTicketActions.length) {
    if (serviceName === Service.OUTBOUND_PROXY) {
      const discardEmailAction = actions.find(
        (action) => action.name === TicketAction.DISCARD_EMAIL
      );
      filteredActions.push({
        name: TicketAction.DISCARD_OUTBOUND_PROXY_EMAIL,
        severity: discardEmailAction?.severity ?? TicketActionSeverity.MEDIUM,
      });
    } else {
      filteredActions.push({
        name: TicketAction.DISCARD_EMAIL_WITH_OPTIONS,
        severity: discardTicketActions.at(0)?.severity ?? TicketActionSeverity.MEDIUM,
      });
    }
    pullAllWith(filteredActions, discardTicketActions, isEqual);
  }

  return filteredActions;
};

/**
 * Determines if quick fix should be shown by validating there are mapped steps
 * and at least one applicable action from the ticket.
 */
export const ticketQuickFixSectionExists = (details: TicketDetails): boolean => {
  const quickFixActions = quickFixStepsMap[details.eventTrigger];
  if (!quickFixActions?.length) {
    return false;
  }

  const availableActions = getTicketActionsList(details.actions, details.sections?.serviceName).map(
    (action) => action.name
  );
  // Ignore MARK_AS_PROCESSED because it's always present and not a valid quick fix signal.
  const quickFixActionsWithoutClose = quickFixActions.filter(
    (action) => action !== TicketAction.MARK_AS_PROCESSED
  );
  return intersection(availableActions, quickFixActionsWithoutClose).length > 0;
};

export interface SubTriggerItem {
  type: string;
  value: string;
}
/**
 * Computes sub-triggers for ticket filtering based on selected event triggers and widget
 * @param eventTriggers - Array of selected event triggers
 * @returns Array of sub-trigger items with type and value
 */
export const getSubTriggers = (eventTriggers: string[] = []): SubTriggerItem[] => {
  const triggers: SubTriggerItem[] = [];
  const userDataGovernanceSubheader = {
    type: "subheader",
    value: i18n.global.t(`ticketDetails.filters.widgets.userDataGovernance`),
  };

  const endpointDataGovernanceSubheader = {
    type: "subheader",
    value: i18n.global.t(`ticketDetails.filters.widgets.endpointDataGovernance`),
  };

  const hasEmailDlpTrigger = userDataGovernanceUnifiedTriggers.some((trigger) =>
    eventTriggers.includes(trigger)
  );
  const hasDeviceDlpTrigger = endpointDataGovernanceUnifiedTriggers.some((trigger) =>
    eventTriggers.includes(trigger)
  );

  if (hasEmailDlpTrigger) {
    triggers.push(userDataGovernanceSubheader);
    userDataGovernanceSubTriggers.forEach((trigger) => {
      triggers.push({
        type: trigger,
        value: trigger,
      });
    });
  }

  if (hasDeviceDlpTrigger) {
    triggers.push(endpointDataGovernanceSubheader);
    endpointDataGovernanceSubTriggers.forEach((trigger) => {
      triggers.push({
        type: trigger,
        value: trigger,
      });
    });
  }

  return triggers;
};

/**
 * Navigates to a related ticket by adding its event ID as a search parameter
 * to the current URL and forcing a page reload.
 *
 * This is used when viewing related tickets from within the ticket preview,
 * where the route doesn't change but the ticket being displayed does.
 *
 * @param eventId - The event ID of the related ticket to navigate to
 *
 * @example
 * // Navigate to related ticket with ID "abc123"
 * navigateToRelatedTicket("abc123");
 * // Results in: current-url?search=abc123 or current-url&search=abc123
 */
export const navigateToRelatedTicket = (eventId: string): void => {
  let url = window.location.href;

  if (url.indexOf("?") > -1) {
    url += `&search=${eventId}`;
  } else {
    url += `?search=${eventId}`;
  }

  window.location.href = url;
};
